// src/App.js
import React, { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { lightTheme, darkTheme } from "./styles/theme";
import {
  markUserDashboardHomeRequiresFreshFetch,
  clearUserPortalDashboardCaches,
} from "./utils/userDashboardHomeCache";
import { clearUserRedemptionStatusCache } from "./utils/userRedemptionStatusCache";
import { prefetchUserRedemptionStatus } from "./utils/userPostLoginPrefetch";
import {
  getOrCreateDeviceIdentity,
  registerUserDevice,
} from "./utils/deviceIdentity";

// Auth
import Login from "./auth/Login";
import Signup from "./auth/Signup";
import VerifyEmail from "./auth/VerifyEmail";
import ForgotPassword from "./auth/ForgotPassword";
import VerifyResetCode from "./auth/VerifyResetCode";
import ResetPassword from "./auth/ResetPassword";

// Dashboards
import UserDashboard from "./user/UserDashboard";
import AdminDashboard from "./admin/AdminDashboard";
import DirectorDashboard from "./director/DirectorDashboard";

// Dashboard pages
import EarningsSummary from "./user/EarningsSummary";
import InviteFriends from "./user/InviteFriends";
import ReferralTree from "./user/ReferralTree";
import TokenLedger from "./user/TokenLedger";
import UpdateProfile from "./user/UpdateProfile";
import WithdrawalHistory from "./user/WithdrawalHistory";
import WithdrawalRequest from "./user/WithdrawalRequest";
import VideoUpload from "./user/VideoUpload";
import UserVideoList from "./user/UserVideoList";
import StripeKYCVerification from "./user/StripeKYCVerification";
import WithdrawHub from './user/WithdrawHub';

// Channels
import ChannelsList from "./user/ChannelsList";
import ChannelDetail from "./user/ChannelDetail";
import ChannelManage from "./user/ChannelManage";
import ChannelEdit from "./user/ChannelEdit";
import VideoEdit from "./user/VideoEdit";

// Component to handle protected route redirects intelligently
function ProtectedRouteRedirect({ isAdmin, isDirector }) {
  const location = useLocation();
  
  // If we're already on an admin route, preserve it (AdminDashboard has catch-all that handles sub-routes)
  // This ensures that when page is refreshed on an admin route, it stays on that route
  if (location.pathname.startsWith('/admin')) {
    // Don't redirect - let AdminDashboard handle the routing
    // The AdminDashboard component has a catch-all route that will show the appropriate page
    // Return a fragment to satisfy React Router
    return <></>;
  }
  
  // If we're already on a director route, preserve it (DirectorDashboard has catch-all that handles sub-routes)
  if (location.pathname.startsWith('/director')) {
    // Don't redirect - let DirectorDashboard handle the routing
    return <></>;
  }
  
  // If we're already on a dashboard route, preserve it (UserDashboard handles sub-routing)
  // This ensures that when page is refreshed on a user dashboard route, it stays on that route
  if (location.pathname.startsWith('/dashboard')) {
    // Don't redirect - let UserDashboard handle the routing
    return <></>;
  }
  
  // Default redirect: Always go to user dashboard for unmatched routes
  // This ensures all users (including admins and directors) land on user dashboard after login
  return <Navigate to="/dashboard" replace />;
}

function getStoredAuthToken() {
  return localStorage.getItem("token") || localStorage.getItem("authToken");
}

function getUserIdFromStoredToken() {
  const t = getStoredAuthToken();
  if (!t) return null;
  try {
    const decoded = jwtDecode(t);
    const id = decoded?.userId ?? decoded?.UserID ?? decoded?.id ?? null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function clearPortalAuthStorage() {
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
}

/** JWT exp is in seconds (UTC). Treat as expired in the last 60s to avoid edge API failures. */
function isAccessTokenExpired(jwt) {
  if (!jwt) return true;
  try {
    const { exp } = jwtDecode(jwt);
    if (exp == null || exp === undefined) return false;
    return Date.now() / 1000 >= exp - 60;
  } catch {
    return true;
  }
}

function readInitialPortalSession() {
  const t = getStoredAuthToken();
  if (!t) {
    return { ok: false, token: null, admin: false, contentCreator: false, director: false };
  }
  if (isAccessTokenExpired(t)) {
    clearPortalAuthStorage();
    return { ok: false, token: null, admin: false, contentCreator: false, director: false };
  }
  try {
    const decoded = jwtDecode(t);
    return {
      ok: true,
      token: t,
      admin: decoded.userTypeId === 1,
      contentCreator: !!decoded.isContentCreator,
      director: decoded.userTypeId === 4,
    };
  } catch {
    clearPortalAuthStorage();
    return { ok: false, token: null, admin: false, contentCreator: false, director: false };
  }
}

function App() {
  const initial = readInitialPortalSession();

  const [loggedIn, setLoggedIn] = useState(initial.ok);
  const [isAdmin, setIsAdmin] = useState(initial.admin);
  const [isContentCreator, setIsContentCreator] = useState(initial.contentCreator);
  const [isDirector, setIsDirector] = useState(initial.director);
  // Always use dark mode
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    try {
      getOrCreateDeviceIdentity();
    } catch {
      /* non-fatal */
    }
  }, []);

  const handleLogout = useCallback(() => {
    const userId = getUserIdFromStoredToken();
    if (userId != null) {
      clearUserPortalDashboardCaches(userId);
      clearUserRedemptionStatusCache(userId);
    }
    clearPortalAuthStorage();
    setLoggedIn(false);
    setIsAdmin(false);
    setIsContentCreator(false);
    setIsDirector(false);
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    const t = getStoredAuthToken();
    if (!t || isAccessTokenExpired(t)) {
      clearPortalAuthStorage();
      setLoggedIn(false);
      setIsAdmin(false);
      setIsContentCreator(false);
      setIsDirector(false);
      return;
    }
    try {
      const decoded = jwtDecode(t);
      setIsAdmin(decoded.userTypeId === 1);
      setIsContentCreator(!!decoded.isContentCreator);
      setIsDirector(decoded.userTypeId === 4);
    } catch {
      clearPortalAuthStorage();
      setLoggedIn(false);
      setIsAdmin(false);
      setIsContentCreator(false);
      setIsDirector(false);
    }
  }, [loggedIn]);

  /** Periodically drop expired sessions (matches API JWT expiry, e.g. 3 days). */
  useEffect(() => {
    if (!loggedIn) return;
    const tick = () => {
      const t = getStoredAuthToken();
      if (!t || isAccessTokenExpired(t)) {
        clearPortalAuthStorage();
        setLoggedIn(false);
        setIsAdmin(false);
        setIsContentCreator(false);
        setIsDirector(false);
        const base = `${window.location.origin}`;
        window.location.assign(`${base}/login`);
      }
    };
    tick();
    const id = setInterval(tick, 60 * 1000);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loggedIn]);

  const handleLogin = useCallback(() => {
    const token = getStoredAuthToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsAdmin(decoded.userTypeId === 1);
        setIsContentCreator(!!decoded.isContentCreator);
        setIsDirector(decoded.userTypeId === 4);
      } catch {
        setIsAdmin(false);
        setIsContentCreator(false);
        setIsDirector(false);
      }
    }
    // Logout clears per-user caches; always fetch fresh dashboard data on login
    // so rank/designation and balances match the server (not a stale bundle).
    markUserDashboardHomeRequiresFreshFetch();
    setLoggedIn(true);
    // Kick off a background prefetch of profile + redemption readiness data
    // so the user's "Important Notice" + Redemptions screen are accurate
    // immediately and we can skip these network calls on subsequent loads.
    try { prefetchUserRedemptionStatus(); } catch { /* non-fatal */ }
    try { registerUserDevice(); } catch { /* non-fatal */ }
  }, []);

  // Mirror the same prefetch for users who land on the app with an existing,
  // still-valid session token (no fresh `handleLogin` call this visit).
  useEffect(() => {
    if (!loggedIn) return;
    try { prefetchUserRedemptionStatus(); } catch { /* non-fatal */ }
    try { registerUserDevice(); } catch { /* non-fatal */ }
  }, [loggedIn]);

  // Dark mode toggle
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const currentTheme = darkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Router>
        <Routes>
        {!loggedIn ? (
          <>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-reset-code" element={<VerifyResetCode />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        ) : (
          <>
            {/* ✅ Dashboard wrapper with nested children */}
            <Route
              path="/dashboard/*"
              element={
                <UserDashboard
                  isAdmin={isAdmin}
                  isDirector={isDirector}
                  isContentCreator={isContentCreator}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  onToggleDarkMode={toggleDarkMode}
                />
              }
            >
              <Route index element={<Navigate to="home" replace />} />
              <Route path="engagement" element={<EarningsSummary />} />
              <Route path="invite" element={<InviteFriends />} />
              <Route path="community" element={<ReferralTree />} />
              <Route path="activity" element={<TokenLedger />} />
              <Route path="profile" element={<UpdateProfile />} />
              <Route path="kyc-verification" element={<StripeKYCVerification />} />
              <Route path="withdrawals" element={<WithdrawalHistory />} />
              <Route path="request" element={<WithdrawalRequest />} />
              <Route path="redemptions" element={<WithdrawHub />} />
              {isContentCreator && (
                <>
                  <Route path="video-upload" element={<VideoUpload />} />
                  <Route path="my-videos" element={<UserVideoList />} />
                </>
              )}
            </Route>

            {/* Admin dashboard */}
            {isAdmin && (
              <Route
                path="/admin/*"
                element={
                  <AdminDashboard 
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    onToggleDarkMode={toggleDarkMode}
                  />
                }
              />
            )}

            {/* Directors dashboard */}
            {isDirector && (
              <Route
                path="/director/*"
                element={
                  <DirectorDashboard 
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    onToggleDarkMode={toggleDarkMode}
                  />
                }
              />
            )}

            <Route path="*" element={<ProtectedRouteRedirect isAdmin={isAdmin} isDirector={isDirector} />} />
          </>
        )}
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
